import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, ChevronLeft, ChevronRight, ListTree, Loader2, Search, X } from "lucide-react";
import api from "../lib/api";

/* Lettore del manuale a pagina intera: indice scuro fisso a sinistra,
   contenuto con l'unica barra di scorrimento, URL per capitolo
   (/formazione/venus/2-5), immagini ingrandibili, ripresa per utente. */

const senzaAccenti = (s) => (s || "").toLowerCase()
  .replace(/[àáâä]/g, "a").replace(/[èéêë]/g, "e").replace(/[ìíîï]/g, "i")
  .replace(/[òóôö]/g, "o").replace(/[ùúûü]/g, "u").replace(/[’']/g, "'");

const estratto = (testo, q) => {
  const i = senzaAccenti(testo).indexOf(q);
  if (i === -1) return [testo.slice(0, 110)];
  const da = Math.max(0, i - 36);
  const a = Math.min(testo.length, i + q.length + 74);
  return [
    (da > 0 ? "…" : "") + testo.slice(da, i),
    <mark key="m">{testo.slice(i, i + q.length)}</mark>,
    testo.slice(i + q.length, a) + (a < testo.length ? "…" : ""),
  ];
};

const Lightbox = ({ img, onClose }) => {
  const [zoom, setZoom] = useState(false);
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className={`wl-man-lightbox ${zoom ? "is-zoom" : ""}`} onClick={onClose} data-testid="manuale-lightbox">
      <button type="button" className="wl-man-lightbox__chiudi" aria-label="Chiudi"
              onClick={onClose} data-testid="manuale-lightbox-chiudi"><X size={19} /></button>
      <div className="wl-man-lightbox__area">
        <img src={img.src} alt={img.caption || "Immagine del manuale"} data-testid="manuale-lightbox-img"
             onClick={(e) => { e.stopPropagation(); setZoom((z) => !z); }} />
      </div>
      {img.caption && (
        <div className="wl-man-lightbox__didascalia" onClick={(e) => e.stopPropagation()}
             data-testid="manuale-lightbox-didascalia">{img.caption}</div>
      )}
    </div>
  );
};

export default function ManualeReader() {
  const { slug, pos } = useParams();
  const navigate = useNavigate();
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState("");
  const [attivoId, setAttivoId] = useState("");
  const [visti, setVisti] = useState(() => new Set());
  const [query, setQuery] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [indiceAperto, setIndiceAperto] = useState(false);

  const contRef = useRef(null);
  const navRef = useRef(null);
  const vistiRef = useRef(new Set());
  const ultimaRef = useRef("");
  const completatoRef = useRef(false);
  const datiRef = useRef(null);
  const timerRef = useRef(null);
  const sessionRef = useRef((crypto.randomUUID && crypto.randomUUID()) || `man-${Date.now()}`);

  /* posizione dall'URL: "2" = capitolo 2, "2-5" = quinta scheda del cap. 2 */
  const posizione = useMemo(() => {
    if (!dati) return null;
    const [c, s] = String(pos || "0").split("-");
    const capNum = Math.max(0, Math.min(dati.capitoli.length - 1, parseInt(c, 10) || 0));
    const capitolo = dati.capitoli.find((k) => k.cap === capNum) || dati.capitoli[0];
    const idx = parseInt(s, 10);
    const scheda = idx >= 1 && idx <= capitolo.schede.length ? capitolo.schede[idx - 1] : null;
    return { capitolo, sezioneId: scheda ? scheda.id : null };
  }, [dati, pos]);

  const posDi = useCallback((id) => {
    for (const c of datiRef.current?.capitoli || []) {
      if (c.id === id) return String(c.cap);
      const i = c.schede.findIndex((s) => s.id === id);
      if (i !== -1) return `${c.cap}-${i + 1}`;
    }
    return "0";
  }, []);

  const inviaProgresso = useCallback(() => {
    const d = datiRef.current;
    if (!d) return;
    const vs = [...vistiRef.current].sort((a, b) => Number(a) - Number(b));
    if (vs.length >= d.tot_capitoli) completatoRef.current = true;
    api.post(`/formazione/progress?session=${sessionRef.current}&course=${d.course.id}`, {
      core: {
        lesson_status: completatoRef.current ? "completed" : "incomplete",
        lesson_location: ultimaRef.current || "",
        score: {},
      },
      suspend_data: `${vs.join(",")}|${ultimaRef.current || ""}`,
    }).catch(() => {});
  }, []);

  const salva = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(inviaProgresso, 1500);
  }, [inviaProgresso]);

  const segna = useCallback((id, cap) => {
    if (id) { ultimaRef.current = id; setAttivoId(id); }
    const k = String(cap);
    if (!vistiRef.current.has(k)) {
      vistiRef.current.add(k);
      setVisti(new Set(vistiRef.current));
    }
    salva();
  }, [salva]);

  /* ── caricamento struttura + stato, cookie per le immagini, ripresa ── */
  useEffect(() => {
    let annullato = false;
    let cookiePath = "";
    (async () => {
      try {
        const [{ data: d }, statoRes] = await Promise.all([
          api.get(`/formazione/manuale/${slug}`),
          api.get(`/formazione/manuale/${slug}/stato`).catch(() => ({ data: null })),
        ]);
        if (annullato) return;
        const token = localStorage.getItem("wattline_token") || "";
        cookiePath = `/api/formazione/scorm/${d.course.id}/`;
        document.cookie = `access_token=${token}; path=${cookiePath}; SameSite=Strict; Secure`;
        datiRef.current = d;
        const st = statoRes?.data;
        if (st) {
          st.visti.forEach((v) => vistiRef.current.add(String(v)));
          setVisti(new Set(vistiRef.current));
          completatoRef.current = !!st.completato;
        }
        setDati(d);
        if (!pos) {
          const riparti = st?.ultima_sezione ? posDiDati(d, st.ultima_sezione) : "0";
          navigate(`/formazione/${slug}/${riparti}`, { replace: true });
        }
      } catch (e) {
        if (!annullato) setErrore(e?.response?.data?.detail || "Manuale non disponibile.");
      }
    })();
    const chiudi = () => inviaProgresso();
    const suNascosto = () => { if (document.visibilityState === "hidden") inviaProgresso(); };
    window.addEventListener("beforeunload", chiudi);
    document.addEventListener("visibilitychange", suNascosto);
    return () => {
      annullato = true;
      window.removeEventListener("beforeunload", chiudi);
      document.removeEventListener("visibilitychange", suNascosto);
      clearTimeout(timerRef.current);
      inviaProgresso();
      if (cookiePath) {
        document.cookie = `access_token=; path=${cookiePath}; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict; Secure`;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  /* ── a ogni cambio di posizione: scroll al punto giusto + segna ── */
  useEffect(() => {
    if (!posizione || !contRef.current) return;
    const cont = contRef.current;
    const vaiAlPunto = () => {
      if (posizione.sezioneId) {
        const el = cont.querySelector(`#${CSS.escape(posizione.sezioneId)}`);
        if (el) {
          el.scrollIntoView({ block: "start" });
          el.classList.remove("puntata");
          void el.offsetWidth;
          el.classList.add("puntata");
          return;
        }
      }
      cont.scrollTop = 0;
    };
    vaiAlPunto();
    const t = setTimeout(vaiAlPunto, 350); // dopo il caricamento delle immagini
    segna(posizione.sezioneId || posizione.capitolo.id, posizione.capitolo.cap);
    // riquadri "da completare"
    cont.querySelectorAll("blockquote").forEach((q) => {
      if (q.textContent.includes("DA COMPLETARE")) q.classList.add("manca");
    });
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posizione]);

  /* ── scrollspy: la sezione in lettura resta evidenziata nell'indice ──
     Scroll-handler deterministico: l'ultima sezione il cui inizio è sopra
     la linea di lettura. (NON IntersectionObserver: la .cap avvolge le
     .scheda e intersecherebbe sempre per prima.) */
  useEffect(() => {
    if (!posizione || !contRef.current) return;
    const cont = contRef.current;
    const sezioni = Array.from(cont.querySelectorAll(".cap[id], .scheda[id]"));
    let inCoda = false;
    const aggiorna = () => {
      inCoda = false;
      const soglia = cont.getBoundingClientRect().top + 130;
      let corrente = sezioni[0];
      for (const s of sezioni) {
        if (s.getBoundingClientRect().top <= soglia) corrente = s;
        else break;
      }
      if (corrente && corrente.id !== ultimaRef.current) {
        segna(corrente.id, posizione.capitolo.cap);
        const btn = navRef.current?.querySelector(".attivo");
        if (btn) btn.scrollIntoView({ block: "nearest" });
      }
    };
    const onScroll = () => {
      if (!inCoda) { inCoda = true; requestAnimationFrame(aggiorna); }
    };
    cont.addEventListener("scroll", onScroll, { passive: true });
    return () => cont.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posizione]);

  const vai = (nuovaPos) => {
    setIndiceAperto(false);
    setQuery("");
    if (nuovaPos === pos) {
      const el = contRef.current?.querySelector(`#${CSS.escape(posizione?.sezioneId || "")}`);
      if (el) el.scrollIntoView({ block: "start" });
      else if (contRef.current) contRef.current.scrollTop = 0;
      return;
    }
    navigate(`/formazione/${slug}/${nuovaPos}`);
  };

  const clickContenuto = (e) => {
    const img = e.target.closest("figure img");
    if (!img) return;
    const fig = img.closest("figure");
    setLightbox({ src: img.src, caption: fig?.querySelector("figcaption")?.textContent || img.alt || "" });
  };

  /* ── ricerca ── */
  const indiceNorm = useMemo(() => (dati?.indice_ricerca || []).map((v) => ({
    ...v, tn: senzaAccenti(v.t), cn: senzaAccenti(v.c),
  })), [dati]);
  const q = senzaAccenti(query.trim());
  const risultati = useMemo(() => {
    if (q.length < 2) return null;
    return indiceNorm
      .map((v) => {
        const pt = v.tn.indexOf(q); const pc = v.cn.indexOf(q);
        return pt === -1 && pc === -1 ? null : { v, peso: pt !== -1 ? pt : 500 + pc };
      })
      .filter(Boolean)
      .sort((a, b) => a.peso - b.peso)
      .slice(0, 30);
  }, [indiceNorm, q]);

  if (errore) {
    return (
      <div className="wl-man"><div className="wl-man__vuoto" data-testid="manuale-errore">
        <AlertCircle size={26} style={{ color: "var(--wl-danger, #C0392B)" }} />
        <div>{errore}</div>
        <Link to="/formazione" className="wl-btn wl-btn--ghost" style={{ border: "1px solid var(--wl-border)" }}>
          <ArrowLeft size={13} /> Torna ai corsi
        </Link>
      </div></div>
    );
  }
  if (!dati || !posizione) {
    return (
      <div className="wl-man"><div className="wl-man__vuoto">
        <Loader2 size={20} className="animate-spin" /><span>Apro il manuale…</span>
      </div></div>
    );
  }

  const { capitolo } = posizione;
  const prec = dati.capitoli.find((c) => c.cap === capitolo.cap - 1);
  const succ = dati.capitoli.find((c) => c.cap === capitolo.cap + 1);
  const n = visti.size;
  const etichetta = n === 0 ? "Nessun capitolo aperto"
    : n === 1 ? `1 capitolo su ${dati.tot_capitoli} aperto`
      : `${n} capitoli su ${dati.tot_capitoli} aperti`;

  return (
    <div className="wl-man" data-testid="manuale-reader">
      <button type="button" className="wl-man__apri-indice" data-testid="manuale-apri-indice"
              onClick={() => setIndiceAperto(true)} aria-expanded={indiceAperto}>
        <ListTree size={16} /> Indice
      </button>
      {indiceAperto && <button type="button" className="wl-man__velo" aria-label="Chiudi l'indice"
                               data-testid="manuale-velo" onClick={() => setIndiceAperto(false)} />}

      <aside className={`wl-man__indice ${indiceAperto ? "is-aperto" : ""}`} data-testid="manuale-indice">
        <div className="wl-man__marchio">
          <Link to="/formazione" className="wl-man__torna" data-testid="manuale-torna-corsi">
            <ArrowLeft size={12} /> Tutti i corsi
          </Link>
          <span className="wl-man__logo">{dati.course.title.split(" ")[0]}</span>
          <span className="wl-man__occhiello">{dati.capitoli[0]?.titolo?.split("—")[1]?.trim() || dati.course.title}</span>
        </div>

        <div className="wl-man__ricerca">
          <Search size={15} className="lente" style={{ position: "absolute", left: 26, top: "50%", transform: "translateY(-38%)", color: "#9A9AA6", pointerEvents: "none" }} />
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
                 placeholder="Cerca nel manuale…" aria-label="Cerca nel manuale"
                 data-testid="manuale-cerca" />
          {query && (
            <button type="button" className="wl-man__pulisci" aria-label="Cancella la ricerca"
                    data-testid="manuale-cerca-pulisci" onClick={() => setQuery("")}>&times;</button>
          )}
        </div>

        {risultati ? (
          <div className="wl-man__esiti" data-testid="manuale-esiti">
            {risultati.length === 0 && <p className="wl-man__niente">Nessun risultato per “{query.trim()}”.</p>}
            {risultati.map(({ v }) => (
              <button key={v.id} type="button" className="wl-man__esito" onClick={() => vai(v.pos)}
                      data-testid={`manuale-esito-${v.id}`}>
                <b>{estratto(v.t, q)}</b>
                <span>{estratto(v.c, q)}</span>
              </button>
            ))}
          </div>
        ) : (
          <nav className="wl-man__nav" ref={navRef} aria-label="Indice del manuale" data-testid="manuale-nav">
            <ul>
              {dati.capitoli.map((c) => (
                <li key={c.id} className={`nav-cap ${visti.has(String(c.cap)) ? "visto" : ""}`}>
                  <button type="button"
                          className={attivoId === c.id ? "attivo" : ""}
                          onClick={() => vai(String(c.cap))}
                          data-testid={`manuale-nav-cap-${c.cap}`}>
                    <span className="wl-man__pallino" aria-hidden="true" />{c.titolo}
                  </button>
                  {capitolo.cap === c.cap && c.schede.length > 0 && (
                    <ul className="nav-schede">
                      {c.schede.map((s, i) => (
                        <li key={s.id}>
                          <button type="button"
                                  className={attivoId === s.id ? "attivo" : ""}
                                  onClick={() => vai(`${c.cap}-${i + 1}`)}
                                  data-testid={`manuale-nav-scheda-${s.id}`}>
                            {s.titolo}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        )}

        <div className="wl-man__avanzamento" data-testid="manuale-avanzamento">
          <div className="wl-man__barra-avanz">
            <span style={{ width: `${Math.round((n / dati.tot_capitoli) * 100)}%` }} />
          </div>
          <p data-testid="manuale-avanzamento-testo">{etichetta}</p>
        </div>
      </aside>

      <div className="wl-man__contenuto" ref={contRef} onClick={clickContenuto} data-testid="manuale-contenuto">
        {/* eslint-disable-next-line react/no-danger */}
        <div className="wl-man__corpo" key={capitolo.cap} dangerouslySetInnerHTML={{ __html: capitolo.html }} />
        <div className="wl-man__piedenav-wrap">
          <div className="wl-man__piedenav">
            {prec ? (
              <button type="button" onClick={() => vai(String(prec.cap))} data-testid="manuale-prev">
                <ChevronLeft size={16} style={{ flexShrink: 0 }} />
                <span><span className="verso">Capitolo precedente</span>{prec.titolo}</span>
              </button>
            ) : <span />}
            {succ ? (
              <button type="button" className="succ" onClick={() => vai(String(succ.cap))} data-testid="manuale-next">
                <span><span className="verso">Capitolo successivo</span>{succ.titolo}</span>
                <ChevronRight size={16} style={{ flexShrink: 0 }} />
              </button>
            ) : <span />}
          </div>
        </div>
      </div>

      {lightbox && <Lightbox img={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

/* posizione URL di una sezione dentro una struttura già caricata */
function posDiDati(d, id) {
  for (const c of d.capitoli) {
    if (c.id === id) return String(c.cap);
    const i = c.schede.findIndex((s) => s.id === id);
    if (i !== -1) return `${c.cap}-${i + 1}`;
  }
  return "0";
}
